import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Chip,
  LinearProgress,
  Fade,
} from '@mui/material';
import {
  Close,
  DragIndicator,
  Add,
  Edit,
  Delete,
  Share,
  Download,
  Settings,
  MoreVert,
  Check,
  Cancel,
} from '@mui/icons-material';

// Mobile-specific hooks
import { usePlatform } from '../../hooks/shared/usePlatform';
import { useHapticFeedback } from '../../hooks/mobile/useHapticFeedback';

/**
 * Mobile-specific Bottom Sheet component
 * Provides slide-up modal functionality for mobile interactions
 */
const BottomSheet = ({
  open = false,
  onClose,
  title = 'Options',
  children,
  maxHeight = '80vh',
  showDragIndicator = true,
  showCloseButton = true,
  variant = 'default', // default, actions, selection, form
  actions = [],
  onAction,
  loading = false,
}) => {
  const { isMobile } = usePlatform();
  const { hapticButton, hapticSuccess } = useHapticFeedback();
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);

  // Handle touch start
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    
    const touch = e.touches[0];
    setDragStartY(touch.clientY);
    setIsDragging(true);
  };

  // Handle touch move
  const handleTouchMove = (e) => {
    if (!isMobile || !isDragging) return;
    
    const touch = e.touches[0];
    const deltaY = touch.clientY - dragStartY;
    
    // Only allow downward dragging
    if (deltaY > 0) {
      setCurrentY(deltaY);
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    if (!isMobile) return;
    
    setIsDragging(false);
    
    // If dragged down more than 100px, close the sheet
    if (currentY > 100) {
      hapticSuccess();
      onClose();
    }
    
    setCurrentY(0);
  };

  // Handle action click
  const handleActionClick = (action) => {
    hapticButton(() => {
      if (onAction) {
        onAction(action);
      }
    }, 'medium')();
  };

  // Render different variants
  const renderContent = () => {
    switch (variant) {
      case 'actions':
        return (
          <List>
            {actions.map((action, index) => (
              <React.Fragment key={action.id || index}>
                <ListItem
                  button
                  onClick={() => handleActionClick(action)}
                  sx={{
                    py: 2,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: action.color || 'primary.main' }}>
                    {action.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={action.label}
                    secondary={action.description}
                    primaryTypographyProps={{ fontWeight: 'medium' }}
                  />
                  {action.badge && (
                    <Chip
                      label={action.badge}
                      size="small"
                      color={action.badgeColor || 'primary'}
                    />
                  )}
                </ListItem>
                {index < actions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        );

      case 'selection':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Select Options
            </Typography>
            <List>
              {actions.map((action, index) => (
                <ListItem
                  key={action.id || index}
                  button
                  onClick={() => handleActionClick(action)}
                  sx={{
                    border: 1,
                    borderColor: action.selected ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: action.selected ? 'primary.main' : 'transparent',
                    color: action.selected ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit' }}>
                    {action.selected ? <Check /> : action.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={action.label}
                    secondary={action.description}
                  />
                </ListItem>
              ))}
            </List>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={onClose}
                startIcon={<Cancel />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleActionClick({ type: 'confirm' })}
                startIcon={<Check />}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        );

      case 'form':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              {title}
            </Typography>
            {children}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleActionClick({ type: 'submit' })}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        );

      default:
        return children;
    }
  };

  if (!isMobile) {
    return null; // Only render on mobile
  }

  return (
    <Fade in={open}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'flex-end',
        }}
        onClick={onClose}
      >
        <Paper
          sx={{
            width: '100%',
            maxHeight: maxHeight,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            backgroundColor: 'background.paper',
            transform: `translateY(${currentY}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease-out',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {showDragIndicator && (
                <DragIndicator sx={{ color: 'text.secondary' }} />
              )}
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {title}
              </Typography>
            </Box>
            {showCloseButton && (
              <IconButton
                onClick={hapticButton(onClose, 'light')}
                size="small"
              >
                <Close />
              </IconButton>
            )}
          </Box>

          {/* Loading indicator */}
          {loading && (
            <LinearProgress sx={{ width: '100%' }} />
          )}

          {/* Content */}
          <Box sx={{ overflow: 'auto', maxHeight: `calc(${maxHeight} - 80px)` }}>
            {renderContent()}
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};

export default BottomSheet;
